import React, { useMemo, useState } from 'react';
import { useOptionalWebSocket } from '../../../contexts/WebSocketContext';
import './TokenPresentationControls.css';

const markerChoices = [
  ['dot', '●', 'Dot'], ['star', '★', 'Star'], ['skull', '☠', 'Skull'],
  ['shield', '◆', 'Shield'], ['eye', '◉', 'Eye'], ['flag', '⚑', 'Flag'], ['number', '#', 'Number'],
];

const TokenPresentationControls = ({ token }) => {
  const { currentSessionId, sendBattleRoomMutation, isGameMaster, currentUser } = useOptionalWebSocket() || {};
  const [pending, setPending] = useState(false);
  const [error, setError] = useState('');
  const [barVisibility, setBarVisibility] = useState('table_exact');
  const [markerColor, setMarkerColor] = useState('red');
  const [markerVisibility, setMarkerVisibility] = useState('table');
  const [markerNumber, setMarkerNumber] = useState('1');
  const bars = useMemo(() => Array.isArray(token?.presentation?.resourceBars) ? token.presentation.resourceBars : [], [token]);
  const markers = useMemo(() => Array.isArray(token?.presentation?.markers) ? token.presentation.markers : [], [token]);
  const resources = (token?.resources ?? token?.entity?.resources ?? []).filter((resource) => (
    resource?.authoritative === true || resource?.mutationAuthority === 'server'
  ));

  const mutate = async (eventName, payload) => {
    if (typeof sendBattleRoomMutation !== 'function') {
      setError('Join a live BattleRoom before changing shared token presentation.');
      return;
    }
    setPending(true);
    setError('');
    try {
      await sendBattleRoomMutation(eventName, { tokenId: token.id, ...payload });
    } catch (cause) {
      setError(cause?.message || 'The token presentation could not be updated.');
    } finally {
      setPending(false);
    }
  };

  const saveBars = (nextBars) => mutate('token:presentation:update', { resourceBars: nextBars.map((bar, index) => ({ ...bar, order: index })) });
  const toggleHp = () => {
    const exists = bars.some((bar) => bar.source?.kind === 'hp');
    saveBars(exists ? bars.filter((bar) => bar.source?.kind !== 'hp') : [...bars, { id: 'hp', source: { kind: 'hp' }, label: 'HP', visibility: barVisibility, colorToken: 'hp', order: bars.length }]);
  };
  const toggleTempHp = () => {
    const exists = bars.some((bar) => bar.source?.kind === 'temp_hp');
    saveBars(exists ? bars.filter((bar) => bar.source?.kind !== 'temp_hp') : [...bars, { id: 'temp-hp', source: { kind: 'temp_hp' }, label: 'Temp HP', visibility: barVisibility, colorToken: 'temp_hp', order: bars.length }]);
  };
  const updateBar = (barId, updates) => saveBars(bars.map((bar) => bar.id === barId ? { ...bar, ...updates } : bar));
  const moveBar = (barId, direction) => {
    const index = bars.findIndex((bar) => bar.id === barId);
    const nextIndex = index + direction;
    if (index < 0 || nextIndex < 0 || nextIndex >= bars.length) return;
    const next = [...bars];
    [next[index], next[nextIndex]] = [next[nextIndex], next[index]];
    saveBars(next);
  };
  const moveMarker = (markerId, direction) => {
    const ids = markers.map((marker) => marker.markerId);
    const index = ids.indexOf(markerId);
    const nextIndex = index + direction;
    if (index < 0 || nextIndex < 0 || nextIndex >= ids.length) return;
    [ids[index], ids[nextIndex]] = [ids[nextIndex], ids[index]];
    mutate('token:marker:reorder', { markerIds: ids });
  };
  const addResource = (resourceKey) => {
    if (!resourceKey || bars.length >= 3) return;
    const resource = resources.find((entry) => String(entry?.key ?? entry?.id) === resourceKey);
    saveBars([...bars, { id: `resource-${resourceKey}`, source: { kind: 'resource', resourceKey }, label: String(resource?.name || resource?.label || resourceKey).slice(0, 24), visibility: barVisibility, colorToken: 'primary', order: bars.length }]);
  };

  if (!token) return null;
  return (
    <section className="br-token-presentation" aria-labelledby={`token-presentation-${token.id}`}>
      <h4 id={`token-presentation-${token.id}`}>Map bars & markers</h4>
      {!currentSessionId && <p className="br-token-presentation__hint">Join a live BattleRoom to share presentation changes.</p>}

      <fieldset disabled={pending || !currentSessionId}>
        <legend>Resource bars</legend>
        <label className="br-token-presentation__check"><input type="checkbox" checked={bars.some((bar) => bar.source?.kind === 'hp')} onChange={toggleHp} disabled={!bars.some((bar) => bar.source?.kind === 'hp') && bars.length >= 3} /> Show HP on map</label>
        <label className="br-token-presentation__check"><input type="checkbox" checked={bars.some((bar) => bar.source?.kind === 'temp_hp')} onChange={toggleTempHp} disabled={!bars.some((bar) => bar.source?.kind === 'temp_hp') && bars.length >= 3} /> Show temporary HP</label>
        <label>New bar visibility
          <select value={barVisibility} onChange={(event) => setBarVisibility(event.target.value)}>
            <option value="table_exact">Table — exact</option>
            <option value="table_banded">Table — banded</option>
            <option value="controller">Controller only</option>
            <option value="gm_only">Game Master only</option>
          </select>
        </label>
        {Array.isArray(resources) && resources.length > 0 && bars.length < 3 && (
          <label>Add authoritative resource
            <select value="" onChange={(event) => addResource(event.target.value)}>
              <option value="">Choose resource…</option>
              {resources.filter((resource) => !bars.some((bar) => String(bar.source?.resourceKey) === String(resource.key ?? resource.id))).map((resource) => (
                <option key={resource.key ?? resource.id} value={resource.key ?? resource.id}>{resource.name ?? resource.label ?? resource.key ?? resource.id}</option>
              ))}
            </select>
          </label>
        )}
        {bars.length > 0 && <ul className="br-token-presentation__list">{bars.map((bar) => (
          <li key={bar.id}><span>{bar.label}</span><select aria-label={`${bar.label} visibility`} value={bar.visibility} onChange={(event) => updateBar(bar.id, { visibility: event.target.value })}><option value="table_exact">Table — exact</option><option value="table_banded">Table — banded</option><option value="controller">Controller only</option><option value="gm_only">Game Master only</option></select><button type="button" aria-label={`Move ${bar.label} up`} disabled={bars[0]?.id === bar.id} onClick={() => moveBar(bar.id, -1)}>↑</button><button type="button" aria-label={`Move ${bar.label} down`} disabled={bars[bars.length - 1]?.id === bar.id} onClick={() => moveBar(bar.id, 1)}>↓</button><button type="button" onClick={() => saveBars(bars.filter((entry) => entry.id !== bar.id))}>Remove</button></li>
        ))}</ul>}
      </fieldset>

      <fieldset disabled={pending || !currentSessionId}>
        <legend>Generic markers</legend>
        <div className="br-token-presentation__options">
          <label>Color<select value={markerColor} onChange={(event) => setMarkerColor(event.target.value)}>{['red', 'amber', 'green', 'blue', 'violet', 'white'].map((color) => <option key={color}>{color}</option>)}</select></label>
          <label>Visible to<select value={markerVisibility} onChange={(event) => setMarkerVisibility(event.target.value)}><option value="table">Table</option><option value="controller">Controller</option><option value="gm_only">Game Master</option></select></label>
          <label>Number<input type="number" min="0" max="99" value={markerNumber} onChange={(event) => setMarkerNumber(event.target.value)} /></label>
        </div>
        <div className="br-token-presentation__palette" aria-label="Add marker">
          {markerChoices.map(([symbolKey, symbol, label]) => <button key={symbolKey} type="button" title={`Add ${label} marker`} aria-label={`Add ${label} marker`} onClick={() => mutate('token:marker:add', { marker: { symbolKey, colorToken: markerColor, label, visibility: markerVisibility, ...(symbolKey === 'number' ? { number: Number(markerNumber) } : {}) } })}>{symbolKey === 'number' ? markerNumber || '#' : symbol}</button>)}
        </div>
        {markers.length > 0 && <ul className="br-token-presentation__list">{markers.map((marker) => (
          <li key={marker.markerId}><span>{marker.label}{marker.number !== undefined ? ` ${marker.number}` : ''} · {marker.colorToken}</span>{isGameMaster && <><button type="button" aria-label={`Move ${marker.label} up`} disabled={markers[0]?.markerId === marker.markerId} onClick={() => moveMarker(marker.markerId, -1)}>↑</button><button type="button" aria-label={`Move ${marker.label} down`} disabled={markers[markers.length - 1]?.markerId === marker.markerId} onClick={() => moveMarker(marker.markerId, 1)}>↓</button></>} {(isGameMaster || String(marker.assignedBy) === String(currentUser?.userId)) && <button type="button" onClick={() => mutate('token:marker:remove', { markerId: marker.markerId })}>Remove</button>}</li>
        ))}</ul>}
        {markers.some((marker) => isGameMaster || String(marker.assignedBy) === String(currentUser?.userId)) && <button className="br-token-presentation__clear" type="button" onClick={() => mutate('token:marker:clear', {})}>{isGameMaster ? 'Clear all markers' : 'Clear my markers'}</button>}
      </fieldset>
      {pending && <p role="status">Saving presentation…</p>}
      {error && <p className="br-token-presentation__error" role="alert">{error}</p>}
    </section>
  );
};

export default TokenPresentationControls;
